// Considered: ChoiceCardGroupAtom for the scope question too — rejected because Section B draws
// the scope question as a two-option pill toggle ("Question 1 — scope (pill toggle)"), which is
// SegmentedControlAtom's shape; ChoiceCardGroupAtom stays for the three-card direction question
// beneath it. See spec Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import ChoiceCardGroupAtom, { ChoiceCardOption } from '../../../atoms/ChoiceCardGroup/ChoiceCardGroupAtom';
import { SegmentedControlAtom, SegmentedControlOption } from '../../../atoms/SegmentedControl';
import MessageAtom from '../../../atoms/Message/MessageAtom';
import { RollupSetModel, RollupSetScope, setDirection, setScope } from '../rollupSetModel';
import './StartFromBand.scss';

export interface StartFromBandProps {
  value: RollupSetModel;
  onChange: (next: RollupSetModel) => void;
  className?: string;
}

/**
 * The design's verbatim inward-warning copy (Section B, board 5) — exported so the acceptance
 * test can assert string equality without duplicating the copy.
 */
export const INWARD_WARNING_COPY =
  'This rolls up spheres above this one. Health normally flows upward, so a sphere that aggregates its own parents will usually double-count itself.';

// Section B, board 5: the summary line beneath the direction cards. `any` scope has one line,
// `related` scope splits by direction. Extracted so the JSX below reads as one assignment.
function resolveSummary(value: RollupSetModel, t: ReturnType<typeof useTranslation>['t']): string {
  if (value.scope === 'any') {
    return t('statusFactors.set.startFrom.summary.any', 'The conditions alone decide — traversal plays no part.');
  }
  if (value.direction === 'inbound') {
    return t('statusFactors.set.startFrom.summary.inbound', 'Add or move a sphere above it and the set follows.');
  }
  return t('statusFactors.set.startFrom.summary.outbound', 'Add or move a sphere beneath it and the set follows.');
}

const StartFromBand: React.FC<StartFromBandProps> = ({ value, onChange, className = '' }) => {
  const { t } = useTranslation();

  const scopeOptions: SegmentedControlOption<RollupSetScope>[] = [
    { value: 'related', label: t('statusFactors.set.startFrom.scope.related.label', 'Related to this sphere') },
    // EAINT-937 (D58): `any` renders as an ordinary pill but stays unselectable until the engine
    // has a rule-source representation for it.
    {
      value: 'any',
      label: t('statusFactors.set.startFrom.scope.any.label', 'Any sphere in the organisation'),
      disabled: true,
      title: t('statusFactors.set.startFrom.scope.any.disabledHint', 'Arriving with EAINT-937.'),
    },
  ];

  const directionOptions: ChoiceCardOption[] = [
    {
      value: 'outbound',
      label: t('statusFactors.set.startFrom.direction.outbound.label', 'Outward'),
      note: t(
        'statusFactors.set.startFrom.direction.outbound.note',
        'Spheres this one reaches, at any depth — what it contains. What almost everyone means.',
      ),
      icon: 'lucide:arrow-down',
    },
    {
      value: 'inbound',
      label: t('statusFactors.set.startFrom.direction.inbound.label', 'Inward'),
      note: t(
        'statusFactors.set.startFrom.direction.inbound.note',
        'Spheres that reach this one — where it is itself assigned. Legal, but unusual.',
      ),
      icon: 'lucide:arrow-up',
    },
    {
      value: 'both',
      label: t('statusFactors.set.startFrom.direction.both.label', 'Both'),
      note: t(
        'statusFactors.set.startFrom.direction.both.disabledNote',
        'Either direction, counted once. Arriving with EAINT-937.',
      ),
      icon: 'lucide:arrow-up-down',
      disabled: true,
    },
  ];

  const summary = resolveSummary(value, t);

  return (
    <div className={`start-from-band ${className}`} data-testid="start-from-band">
      <div className="start-from-band__header">
        <span className="start-from-band__label">{t('statusFactors.set.startFrom.label', 'Start from')}</span>
        <span className="start-from-band__caption">
          {t('statusFactors.set.startFrom.caption', 'which spheres are even candidates')}
        </span>
      </div>

      <SegmentedControlAtom
        options={scopeOptions}
        value={value.scope}
        onChange={(next) => onChange(setScope(value, next as RollupSetScope))}
        mode="single"
        variant="pills"
        size="small"
        ariaLabel={t('statusFactors.set.startFrom.scope.ariaLabel', 'Scope')}
      />

      {value.scope === 'related' && (
        <>
          <div className="start-from-band__direction-heading">
            {t('statusFactors.set.startFrom.directionHeading', 'WHICH WAY DO WE TRAVERSE?')}
          </div>
          <ChoiceCardGroupAtom
            options={directionOptions}
            value={value.direction}
            onChange={(next) => onChange(setDirection(value, next as 'inbound' | 'outbound'))}
            ariaLabel={t('statusFactors.set.startFrom.directionHeading', 'WHICH WAY DO WE TRAVERSE?')}
            layout="inline"
          />
        </>
      )}

      <p className="start-from-band__summary">{summary}</p>

      {value.scope === 'related' && value.direction === 'inbound' && (
        <MessageAtom
          severity="warn"
          text={t('statusFactors.set.startFrom.inwardWarning', INWARD_WARNING_COPY)}
          className="start-from-band__inward-warning"
        />
      )}
    </div>
  );
};

StartFromBand.displayName = 'StartFromBand';

export default StartFromBand;
